import { back, push } from '@lagunovsky/redux-react-router'
import { all, call, put, select, takeLatest } from 'redux-saga/effects'

import { PATH_NAME } from '../../app/constants'
import { pathnameSelector } from '../../app/selectors'

import { setErrorMessage } from 'components/Windows/CentralMessagesList/reducer'
import jwtDecode from 'jwt-decode'
import { callApiAsync } from 'services/api/callAPIAsync'
import { setAuthorized } from '../../services/auth/reducer'
import { setUser } from '../../services/user/reducer'
import { getCinestarClub } from '../UserProfile/components/CinestarClub/reducer'
import { getAccountOrders } from '../UserProfile/components/TicketsAndReserv/reducer'

import { startLoading, stopLoading } from 'components/Loader/LoaderPopup/reducer'
import gtmLoginError from 'services/gtm/gtmLoginError'
import gtmLoginSuccess from 'services/gtm/gtmLoginSuccess'
import { setCookie } from 'services/token/helpers'
import { checkAccountId } from 'services/user/checkAccountId'
import {
    checkLogin,
    login,
    loginUsingApple,
    loginUsingFacebook,
    loginUsingGoogle,
    setError,
    setLoading,
} from './reducer'

function* redirectAfterLogin() {
    const pathname = yield select(pathnameSelector)
    if (pathname === PATH_NAME.CONTACT_INFO) {
        yield put(push(PATH_NAME.ORDER_SUMMARY))
    } else if (pathname === PATH_NAME.BUY_TICKET_VOUCHER) {
        return
    } else {
        yield put(push(PATH_NAME.USER_PROFILE))
    }
}

function* loginCallSaga(action) {
    const email = action?.payload?.email
    const password = action?.payload?.password

    yield put(setLoading(true))
    yield put(startLoading())
    const { data, error } = yield call(callApiAsync, 'loginAuthorize', { email, password })

    if (error && error !== 'E_API_ALREADY_AUTHORIZED') {
        yield put(stopLoading())
        yield put(setLoading(false))
        yield put(setErrorMessage(error))
        console.error(error)
        gtmLoginError()
        return
    }

    yield put(setUser(data))
    localStorage.setItem('userEmail', data?.email)
    gtmLoginSuccess()

    yield put(setLoading(false))
    yield call(redirectAfterLogin)
    yield put(stopLoading())
    yield put(setAuthorized(true))
}

function* checkLoginCallSaga() {
    const { data, error } = yield call(callApiAsync, 'getAccount')
    if (error) {
        yield put(setErrorMessage(error))
        yield all([put(setAuthorized(false)), put(setUser(null))])
        console.error(error)
        return
    }
    if (data.accountid < 0) {
        yield put(setAuthorized(false))
        return
    }
    checkAccountId(data.accountid)
    yield all([put(setAuthorized(true)), put(setUser(data)), put(getCinestarClub()), put(getAccountOrders())])
}

function* googleLoginCallSaga(action) {
    const provider = 'google'
    const provider_data = action?.payload?.credential
    const userData = jwtDecode(provider_data)

    setCookie('profilePicture', userData.picture)

    const pathname = yield select(pathnameSelector)

    yield put(setLoading(true))
    yield put(setError(null))

    const { data, error } = yield call(callApiAsync, 'loginAuthorize', { provider, provider_data })
    yield put(setLoading(false))
    if (error && error !== 'E_API_ALREADY_AUTHORIZED') {
        yield put(setLoading(false))
        yield put(setErrorMessage(error))
        console.error(error)
        gtmLoginError()
        return
    }

    localStorage.setItem('userEmail', data?.email)
    gtmLoginSuccess()

    yield all([put(setUser(data)), put(setAuthorized(true)), put(getCinestarClub()), put(getAccountOrders())])

    pathname === PATH_NAME.CONTACT_INFO ? yield put(push(PATH_NAME.ORDER_SUMMARY)) : yield put(back())
}

function* facebookLoginCallSaga(action) {
    const provider = 'facebook'
    const imgUrl = `http://graph.facebook.com/${action?.payload.userID}/picture?type=square`
    setCookie('profilePicture', imgUrl)
    const provider_data = action?.payload?.accessToken
    const pathname = yield select(pathnameSelector)

    yield put(setLoading(true))
    yield put(setError(null))

    const { data, error } = yield call(callApiAsync, 'loginAuthorize', { provider, provider_data })
    yield put(setLoading(false))
    if (error && error !== 'E_API_ALREADY_AUTHORIZED') {
        gtmLoginError()
        yield put(setLoading(false))
        yield put(setErrorMessage(error))
        console.error(error)
        return
    }

    localStorage.setItem('userEmail', data?.email)
    gtmLoginSuccess()

    yield all([put(setUser(data)), put(setAuthorized(true)), put(getCinestarClub()), put(getAccountOrders())])

    pathname === PATH_NAME.CONTACT_INFO ? yield put(push(PATH_NAME.ORDER_SUMMARY)) : yield put(back())
}

function* appleLoginCallSaga({ payload }) {
    const provider = 'apple'
    const provider_data = payload.detail.authorization.id_token
    yield put(setLoading(true))
    const pathname = yield select(pathnameSelector)
    const { data, error } = yield call(callApiAsync, 'loginAuthorize', { provider, provider_data })
    yield put(setLoading(false))
    if (error && error !== 'E_API_ALREADY_AUTHORIZED') {
        gtmLoginError()
        yield put(setErrorMessage(error))
        console.error(error)
        return
    }

    localStorage.setItem('userEmail', data?.email)
    gtmLoginSuccess()

    yield all([put(setUser(data)), put(setAuthorized(true)), put(getCinestarClub()), put(getAccountOrders())])
    pathname === PATH_NAME.CONTACT_INFO ? yield put(push(PATH_NAME.ORDER_SUMMARY)) : yield put(back())
}

export default function* loginSaga() {
    yield all([
        takeLatest(login.type, loginCallSaga),
        takeLatest(checkLogin.type, checkLoginCallSaga),
        takeLatest(loginUsingGoogle.type, googleLoginCallSaga),
        takeLatest(loginUsingFacebook.type, facebookLoginCallSaga),
        takeLatest(loginUsingApple.type, appleLoginCallSaga),
    ])
}
